import { Component, ElementRef, inject, signal, viewChild } from '@angular/core'
import { VisioPopupService } from '@visio-ai/ui-kit'
import { Kanban } from '../../../_services/kanban/kanban.model'
import { PvtKanbanService } from '../../../_services/kanban/kanban.service'
import {
  ActionsConfig,
  ActionsConfigProps,
  FieldConfig
} from '../entities/kanban-card/kanban-card.component'
import { KanbanFilter } from '../widgets/kanban-filters/kanban-filters.model'
import { KanbanPermissionService } from '../services/kanban-permission.service'

export interface KanbanWebcomponent {
  initBoard(): void
  moveCardLocally(cardId: string, sourcePhaseId: string, destinationPhaseId: string): void
  createCard(card: Kanban.Card.Entity): void
}

@Component({
  selector: 'app-kanban-page',
  standalone: false,
  templateUrl: './kanban-page.component.html',
  styleUrl: './kanban-page.component.scss'
})
export class KanbanPageComponent {
  // Using the same boardId as in the kanban-test component
  readonly boardId = 'board-review-pista'
  private popupService = inject(VisioPopupService)
  private pvtKanbanService = inject(PvtKanbanService)
  private kanbanPermissionService = inject(KanbanPermissionService)
  private kanbanRef = viewChild<ElementRef<KanbanWebcomponent>>('kanban')

  protected canAccessBoard = signal(false)

  /*
   * COMO UTILIZAR:
   * Altere o boardId para o ID do board kanban que deseja utilizar.
   *
   * Para customizar colunas, criar um Record associando o ID da coluna com um
   * objeto do tipo ActionsConfigProps.
   *
   * O campo 'secondary' define a ação secundária que aparece ao lado do botão
   * 'Ver detalhes'
   *
   * Caso queira adicionar mais ações, utilizar o campo 'others', que é um
   * array de objetos do tipo ListElement.
   *
   * Todas as ações adicionais aceitam o campo 'onClick' para definir callbacks
   * customizados.
   */

  readonly phases = [
    'backlog-review_pista',
    'review_pista',
    'backlog-liberado',
    'liberado',
    'backlog-falha',
    'falha',
    'blocked',
    'failed',
    'finished',
    'released'
  ] as const

  readonly failTaskAction: ActionsConfig = {
    label: 'Reportar falha',
    onClick: (task: Kanban.Card.Entity) =>
      this.popupService.callPopup(
        {
          title: 'Reportar falha',
          body: 'Corpo do erro',
          id: 'kanban-popup',
          type: 'error'
        },
        (action: { accept: boolean }) => {
          if (action.accept) {
            const destination = task.phaseId === 'review-pista' ? 'blocked' : 'review-pista'
            this.kanbanRef()?.nativeElement.moveCardLocally(task.id, task.phaseId, destination)
          }
        }
      )
  }

  readonly logCardAction: ActionsConfig = {
    label: 'Log',
    onClick: (task: Kanban.Card.Entity) => {
      console.log(task)
    }
  }

  fieldsToShow: FieldConfig[] = [
    { id: 'failedReason' },
    {
      id: 'country',
      pipe: (value: any): string => {
        if (!value) return '-'
        const country = new Intl.DisplayNames(['pt'], { type: 'region' })
        return country.of(value)!
      }
    }
  ]

  columnActions: Partial<Record<(typeof this.phases)[number], ActionsConfigProps>> = {
    review_pista: {
      secondary: this.failTaskAction,
      primary: this.logCardAction,
      others: [{
        item: 'ALERTA DE RESENHA',
        onClick: () => alert('RESENHA')
      }]
    },
    blocked: {
      secondary: this.failTaskAction,
      primary: this.logCardAction
    },
    finished: {
      secondary: this.failTaskAction,
      primary: this.logCardAction
    },
    failed: {
      primary: this.logCardAction
    }
  }

  filters: KanbanFilter[] = [
    {
      type: 'select',
      field: {
        fixed: false,
        id: 'product'
      },
      label: 'Produto',
      options: [
        { label: 'Checkout', value: 'checkout' },
        { label: 'Pista', value: 'pista' }
      ]
    }
  ]

  constructor() {
    this.kanbanPermissionService.checkAccess().subscribe(canRead => {
      if (!canRead) {
        this.popupService.callPopup({
          title: 'Erro',
          body: 'Você não tem permissão para acessar este board.',
          type: 'error',
          id: 'kanban-popup'
        })
        return
      }

      const canWriteCard = this.kanbanPermissionService.canWriteCard()
      const failTaskAction: ActionsConfig = { ...this.failTaskAction, isAllowed: canWriteCard }

      this.columnActions = {
        review_pista: {
          secondary: failTaskAction,
          primary: this.logCardAction,
          others: [{
            item: 'ALERTA DE RESENHA',
            onClick: () => alert('RESENHA')
          }]
        },
        blocked: {
          secondary: failTaskAction,
          primary: this.logCardAction
        },
        finished: {
          secondary: failTaskAction,
          primary: this.logCardAction
        },
        failed: {
          primary: this.logCardAction
        }
      }

      this.canAccessBoard.set(true)
    })
  }
}
